/**
 * Keyboard Shortcuts Hook
 * Handles all keyboard shortcuts for the workflow editor
 */

import { useEffect } from 'react';
import { useWorkflowStore } from '@flow-studio/store';
import { useUIStore } from '@flow-studio/store';
import { isAnyModalOpen } from '../components/Support/Modal';

export interface UseKeyboardShortcutsOptions {
  /** Suppress the Delete/Backspace deletion shortcut (e.g. while the hand tool is active). */
  deleteDisabled?: boolean;
}

export const useKeyboardShortcuts = ({ deleteDisabled = false }: UseKeyboardShortcutsOptions = {}) => {
  const { undo, redo, canUndo, canRedo, nodes, edges, saveWorkflowToApi, isDirty, isSaving } = useWorkflowStore();
  const {
    selectedNodes,
    selectedEdges,
    clearSelection,
    clipboard,
    setClipboard,
    showToast,
    requestDeleteSelected,
  } = useUIStore();

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const cmdOrCtrl = isMac ? event.metaKey : event.ctrlKey;

      // Prevent default for shortcuts
      const preventDefault = () => {
        event.preventDefault();
        event.stopPropagation();
      };

      // Don't hijack native browser shortcuts (copy/paste/select-all/undo/redo)
      // while the user is typing in a form field, dialog input, etc.
      if (isInputFocused()) {
        return;
      }

      // Undo: Ctrl+Z or Cmd+Z
      if (cmdOrCtrl && event.key === 'z' && !event.shiftKey && canUndo()) {
        preventDefault();
        undo();
        return;
      }

      // Redo: Ctrl+Shift+Z or Cmd+Shift+Z or Ctrl+Y
      if (
        ((cmdOrCtrl && event.shiftKey && event.key === 'z') ||
          (cmdOrCtrl && event.key === 'y')) &&
        canRedo()
      ) {
        preventDefault();
        redo();
        return;
      }

      // Copy: Ctrl+C or Cmd+C
      if (cmdOrCtrl && event.key === 'c' && selectedNodes.length > 0) {
        preventDefault();
        const nodesToCopy = nodes.filter((node) =>
          selectedNodes.includes(node.id)
        );
        const edgesToCopy = edges.filter(
          (edge) =>
            selectedNodes.includes(edge.source) &&
            selectedNodes.includes(edge.target)
        );
        setClipboard({ nodes: nodesToCopy, edges: edgesToCopy });
        return;
      }

      // Paste: Ctrl+V or Cmd+V
      if (cmdOrCtrl && event.key === 'v' && clipboard && clipboard.nodes.length > 0) {
        preventDefault();
        // Note: Actual paste logic would be handled in the component
        // This just prevents default browser paste
        return;
      }

      // Select All: Ctrl+A or Cmd+A
      if (cmdOrCtrl && event.key === 'a') {
        preventDefault();
        // Note: Select all logic handled in component
        return;
      }

      // Delete: Delete or Backspace — route through confirmation dialog
      if (
        !deleteDisabled &&
        (event.key === 'Delete' || event.key === 'Backspace') &&
        (selectedNodes.length > 0 || selectedEdges.length > 0)
      ) {
        preventDefault();
        requestDeleteSelected(selectedNodes, selectedEdges);
        return;
      }

      // Escape: Clear selection. While a dialog is open, Escape belongs to that dialog, so the
      // canvas selection is left alone. A dialog that handled this Escape has already closed and
      // marked the event, so check that too; this listener runs after the dialog's own listener.
      if (event.key === 'Escape') {
        if (!event.defaultPrevented && !isAnyModalOpen()) clearSelection();
        return;
      }

      // Save: Ctrl+S or Cmd+S
      if (cmdOrCtrl && event.key === 's') {
        preventDefault();

        if (!isDirty) {
          showToast('info', 'No changes to save');
          return;
        }

        if (isSaving) {
          showToast('info', 'Save already in progress...');
          return;
        }

        console.log('💾 [Keyboard] Ctrl+S pressed - saving workflow');

        // Trigger save
        saveWorkflowToApi().then((result) => {
          if (result) {
            showToast('success', 'Workflow saved successfully!');
          } else {
            showToast('error', 'Failed to save workflow');
          }
        }).catch((error) => {
          console.error('Save error:', error);
          showToast('error', `Save failed: ${error.message}`);
        });

        return;
      }
    };

    // Helper: Check if input is focused
    const isInputFocused = (): boolean => {
      const activeElement = document.activeElement;
      return (
        activeElement instanceof HTMLInputElement ||
        activeElement instanceof HTMLTextAreaElement ||
        activeElement?.getAttribute('contenteditable') === 'true'
      );
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [
    undo,
    redo,
    canUndo,
    canRedo,
    nodes,
    edges,
    selectedNodes,
    selectedEdges,
    clipboard,
    clearSelection,
    setClipboard,
    requestDeleteSelected,
    saveWorkflowToApi,
    isDirty,
    isSaving,
    showToast,
  ]);
};
